import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ContactDataDto } from '@bo-client/shared/models/contactDataDto';
import { ProducerModel } from '@bo-client/activation-management/grid-registration/model/producer.model';
import { ExistingFeederModel } from '@bo-client/activation-management/grid-registration/model/existing-feeder.model';
import { ConnectionObjectAdditionalInfoModel } from '@bo-client/activation-management/shared/models/connection-object-additional-info.model';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { BankAccountModel } from '@bo-client/shared/models/bankAccountModel';
import { SepaMandateModel } from '@bo-client/shared/models/sepaMandateModel';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import {
  RegexToCheckMaStrNumber,
  RegexToCheckModuleMaStrNumber
} from '@bo-client/shared/forms/forms.validators';

@Injectable({
  providedIn: 'root'
})
export class CreateFormsFcContractConfirmationService {
  public form = new FormGroup({}, { updateOn: 'submit' });

  constructor(
    private fb: FormBuilder,
    private createFormsService: CreateFormsService,
    private dataShareService: DataShareService,
    private createFormsGridRegistrationService: CreateGridRegistrationFormsService // eslint-disable-next-line no-empty-function
  ) {}

  createFeederContractForm(data: FeederContractModel) {
    const customerDataList: ContactDataDto[] = [data.customer];
    const invoiceDataContactList: ContactDataDto[] = [data.invoiceContact];
    const hasDifferentInvContact =
      !!data.invoiceContact && (!!data.invoiceContact.firstName || !!data.invoiceContact.company);

    if (!data.connectionObjectAdditionalInfo) {
      data.connectionObjectAdditionalInfo = new ConnectionObjectAdditionalInfoModel();
    }
    data.bankAccount = data.bankAccount ? data.bankAccount : new BankAccountModel();
    return this.fb.group({
      id: [data.id],
      state: [data.state],
      contractType: [data.contractType],
      stateModifiedDate: [data.stateModifiedDate],
      commissioningDate: [data.commissioningDate, Validators.required],
      meterInstallationDate: [data.meterInstallationDate],
      deviceNo: [data.deviceNo],
      compensationCategories: [data.compensationCategories],

      plantOperatorAndEndUserAreIdentical: [data.plantOperatorAndEndUserAreIdentical],
      maStRNumberProducer: [data.maStRNumberProducer],
      maStRNumberStorage: [data.maStRNumberStorage],
      maStRNumber: [data.maStRNumber],
      maStRAcceptedDeclaration: [data.maStRAcceptedDeclaration],
      maStRRegistrationDate: [data.maStRRegistrationDate],
      // New fields
      maStRSELNumber: [data.maStRSELNumber], // MaStR Nr. der Stromerzeugungslokation der Einheit !
      maStREEGNumber: [data.maStREEGNumber], // MaStR Nr. der Erzeugungsanlage (EEG Nummer) !
      maStRSSENumberStorage: [data.maStRSSENumberStorage], // MaStR SSE des Speichers !
      maStREEGNumberStorage: [data.maStREEGNumberStorage], // MaStR EEG der Erzeugungsanlage (SEE Nummer) !

      batteryTechnology: [data.batteryTechnology], // Batterietechnologie
      batteryType: [data.batteryType], // Batterietyp
      usageType: [data.usageType], // MaStR Nutzungsart
      orientation: [data.orientation], // Ausrichtung
      tilt: [data.tilt], // Neigung
      secondaryOrientation: [data.secondaryOrientation], // Nebenausrichtung
      secondaryTilt: [data.secondaryTilt], // Neigung Nebenausrichtung

      ustNr: [data.ustNr],
      taxNr: [data.taxNr],
      meterOperator: [data.meterOperator, Validators.required],
      ustPflichtig: [data.ustPflichtig],
      steuerArt: [data.steuerArt],
      bankAccount: this.createBankAccountForm(data.bankAccount, true),
      paymentType: [data.paymentType],
      sepaMandate: this.createSepaMandateForm(data.sepaMandate),
      acceptedUstConditions: [data.acceptedUstConditions],
      shortDescription: [data.shortDescription],
      longDescription: [data.longDescription],
      externalId: [data.externalId],
      externalGridRegistrationId: [data.externalGridRegistrationId],
      feedInManagementInfo: [data.feedInManagementInfo],
      powerGenerationSource: [data.powerGenerationSource],
      isEEGExtention: [data.isEEGExtention],
      feedingPlantType: [data.feedingPlantType],
      consumptionOver30kw: [data.consumptionOver30kw],
      comment: [data.comment],
      hasStorage: [data.hasStorage],
      connectionObject: this.createFormsService.connectionObjectForm(data.connectionObject, data.uniqueCoId),
      additionalInfoConnectionObject: this.additionalInfoConnectionObjectGridRegistrationForm(
        data.connectionObjectAdditionalInfo
      ),
      filiationData: this.fb.array(
        customerDataList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      hasDifferentInvoiceContact: [hasDifferentInvContact],
      invoiceContact: this.fb.array(
        invoiceDataContactList.map((data) => this.createFormsService.createContactDataForm(data, true))
      ),
      pvModules: this.fb.array(
        data.pvModules.map((data) => this.createFormsGridRegistrationService.createPVModuleForm(data))
      ),
      producers: this.fb.array(
        data.producers.map((data) => this.createFormsGridRegistrationService.createProducerForm(data))
      ),
      storages: this.fb.array(
        data.storages.map((data) => this.createFormsGridRegistrationService.createStorageForm(data))
      ),
      uploads: this.fb.array(data.uploads.map((data) => this.createFormsService.createUploadForm(data)))
    });
  }

  createExistingFeederForm(data: ExistingFeederModel) {
    return this.fb.group({
      id: [data.id],
      powerGenerationSource: [data.powerGenerationSource],
      power: [data.power]
    });
  }

  additionalInfoConnectionObjectGridRegistrationForm(data: ConnectionObjectAdditionalInfoModel) {
    return this.fb.group({
      isNewBuilding: [data.isNewBuilding],
      buildingType: [data.buildingType],
      numberOfFlats: [data.numberOfFlats],
      numberOfUsers: [data.numberOfUsers],
      numberOfOtherUsers: [data.numberOfOtherUsers],
      objectUsage: [data.objectUsage]
    });
  }

  checkIfHasProducers(listProducers: ProducerModel[]): boolean {
    if (listProducers.length > 1) {
      return true;
    }
    if (listProducers.length == 1) {
      if (listProducers[0].count > 0 && listProducers[0].acActivePowerMax > 0) {
        return true;
      }
    }
    return false;
  }

  createFCContractConfirmationOfferForm(data: FeederContractModel) {
    const invoiceDataContactList: ContactDataDto[] = [data.invoiceContact];
    if (!data.bankAccount) {
      data.bankAccount = new BankAccountModel();
    }
    const hasDifferentInvContact = data.invoiceContact && data.invoiceContact.firstName;
    return this.fb.group({
      commissioningDate: [data.commissioningDate],
      meterInstallationDate: [data.meterInstallationDate],
      deviceNo: [data.deviceNo],
      compensationCategories: [data.compensationCategories],
      plantOperatorAndEndUserAreIdentical: [data.plantOperatorAndEndUserAreIdentical],
      maStRNumberProducer: [
        data.maStRNumberProducer,
        data.producers && data.producers.length > 0
          ? [Validators.required, Validators.pattern(RegexToCheckModuleMaStrNumber)]
          : []
      ],
      maStRNumberStorage: [
        data.maStRNumberStorage,
        data.storages && data.storages.length > 0
          ? [Validators.required, Validators.pattern(RegexToCheckModuleMaStrNumber)]
          : []
      ],
      maStRNumber: [data.maStRNumber, [Validators.required, Validators.pattern(RegexToCheckMaStrNumber)]],
      maStRAcceptedDeclaration: [
        data.maStRAcceptedDeclaration,
        [Validators.required, this.createFormsService.mustTrue]
      ],
      maStRRegistrationDate: [data.maStRRegistrationDate, Validators.required],
      contractType: [data.contractType],
      meterOperator: [data.meterOperator, Validators.required],
      ustNr: [data.ustNr],
      taxNr: [data.taxNr],
      ustPflichtig: [data.ustPflichtig, [Validators.required]],
      steuerArt: [data.steuerArt],
      bankAccount: this.createBankAccountForm(data.bankAccount),
      paymentType: [data.paymentType, [Validators.required]],
      sepaMandate: this.createSepaMandateForm(data.sepaMandate),
      hasDifferentInvoiceContact: [hasDifferentInvContact],
      companyInDistressCheck01: [
        null,
        this.dataShareService.tenantConfig?.feederContract?.companyInDistressCheck
          ? [Validators.requiredTrue]
          : []
      ],
      companyInDistressCheck02: [
        null,
        this.dataShareService.tenantConfig?.feederContract?.companyInDistressCheck
          ? [Validators.requiredTrue]
          : []
      ],
      invoiceContact: this.fb.array(
        invoiceDataContactList.map((data) => this.createFormsService.createContactDataForm(data))
      ),
      producers: this.fb.array(
        data.producers.map((data) => this.createFormsGridRegistrationService.createProducerForm(data))
      ),
      storages: this.fb.array(
        data.storages.map((data) => this.createFormsGridRegistrationService.createStorageForm(data))
      ),
      acceptedUstConditions: [
        data.acceptedUstConditions,
        [Validators.required, this.createFormsService.mustTrue]
      ]
    });
  }

  createBankAccountForm(data: BankAccountModel, notRequired?: boolean) {
    if (!data) {
      data = {};
    }
    return this.fb.group({
      iban: [data.iban, notRequired ? [] : Validators.required],
      bic: [data.bic],
      holder: [data.holder],
      bankName: [data.bankName]
    });
  }

  createSepaMandateForm(data: SepaMandateModel) {
    if (!data) {
      data = {};
    }
    return this.fb.group({
      acceptedSepa: [data.acceptedSepa],
      sepaAcceptedDate: [data.sepaAcceptedDate],
      validFrom: [data.validFrom],
      validTo: [data.validTo]
    });
  }
}
